/**
 * F4 — Web Push feliratkozás kezelő hook
 *
 * iOS korlát: push csak iOS 16.4+ AND telepített PWA-ban működik.
 * Engedélykérés: NEM betöltéskor, hanem amikor a hívó hely igényli (pl. első értesítés-akció).
 */
import { useState, useEffect, useCallback } from 'react';
import { get, post } from '../lib/api';

// ── iOS detektálás ───────────────────────────────────────────────────────────
function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isIOSSupportedForPush(): boolean {
  if (!isIOS()) return false;
  const match = navigator.userAgent.match(/OS (\d+)_/);
  const version = match ? parseInt(match[1], 10) : 0;
  return version >= 16;
}

function isInstalledPWA(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export type PushState =
  | 'unsupported'      // böngésző nem támogatja
  | 'ios_not_installed'// iOS-on nincs telepítve (csak Add to Home Screen kell)
  | 'ios_old'          // iOS < 16.4, nem támogatott
  | 'denied'           // felhasználó megtagadta
  | 'subscribed'       // aktív feliratkozás
  | 'idle';            // engedély még nem kért

interface UsePushReturn {
  pushState: PushState;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  loading: boolean;
  subscribeError: string | null;
}

export function usePush(): UsePushReturn {
  const [pushState, setPushState] = useState<PushState>('idle');
  const [loading, setLoading] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);

  useEffect(() => {
    detectState();
  }, []);

  async function detectState() {
    // Böngésző támogatás
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      if (isIOS()) {
        if (isIOSSupportedForPush()) {
          setPushState(isInstalledPWA() ? 'idle' : 'ios_not_installed');
        } else {
          setPushState('ios_old');
        }
      } else {
        setPushState('unsupported');
      }
      return;
    }

    // iOS 16.4+ de nincs telepítve
    if (isIOS() && !isInstalledPWA()) {
      setPushState('ios_not_installed');
      return;
    }

    const perm = await navigator.permissions.query({ name: 'notifications' as PermissionName });
    if (perm.state === 'denied') { setPushState('denied'); return; }

    // Ellenőrizzük hogy van-e már aktív subscription a SW-ben
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    setPushState(sub ? 'subscribed' : 'idle');
  }

  const subscribe = useCallback(async () => {
    setLoading(true);
    setSubscribeError(null);
    try {
      // VAPID public key lekérése
      const { enabled, publicKey } = await get<{ enabled: boolean; publicKey?: string }>('/push/vapid-key');
      if (!enabled || !publicKey) { setPushState('unsupported'); setLoading(false); return; }

      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushState('denied'); setLoading(false); return; }

      // serviceWorker.ready timeout: ha 10 mp alatt nem érhető el → feladjuk
      const swTimeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('SW timeout')), 10000),
      );
      const reg = await Promise.race([navigator.serviceWorker.ready, swTimeout]);

      // pushManager.subscribe timeout: 15 mp
      const subTimeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Push subscribe timeout')), 15000),
      );
      const sub = await Promise.race([
        reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }),
        subTimeout,
      ]);

      await post('/push/subscribe', {
        endpoint: sub.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(sub.getKey('p256dh')!),
          auth: arrayBufferToBase64(sub.getKey('auth')!),
        },
        userAgent: navigator.userAgent,
      });

      setPushState('subscribed');
    } catch (e) {
      console.error('[Push] subscribe hiba:', e);
      setPushState('idle');
      const msg = e instanceof Error ? e.message : 'Ismeretlen hiba';
      setSubscribeError(msg.includes('timeout') ? 'Időtúllépés — ellenőrizd az internetkapcsolatot.' : 'Feliratkozás sikertelen. Ellenőrizd a böngésző értesítési beállításait.');
    } finally {
      setLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await post('/push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setPushState('idle');
    } catch (e) {
      console.error('[Push] unsubscribe hiba:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  return { pushState, subscribe, unsubscribe, loading, subscribeError };
}

// ── Segédfüggvények ──────────────────────────────────────────────────────────
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}
